import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addMemberToGroup } from "@/helpers/apis";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  TooltipContent,
  TooltipCore,
  TooltipTrigger,
  toast,
} from "@altimateai/lego";
import { useState } from "react";
import { LoadingButton, Stack } from '@altimateai/lego'
import { AddIcon } from "@/assets/icons";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  groupId: string;
  onSuccess?: () => void;
}

const AddNewMember = ({ groupId, onSuccess }: Props) => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const { mutate: addMember, isPending: isLoading } = useMutation({
    mutationFn: (data: FormValues) => addMemberToGroup(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["UserGroupDetails", groupId] });
      toast({ description: "Member added successfully" });
      setOpen(false);
      form.reset();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        description: `Failed to add member: ${error.message || error}`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormValues) => {
    addMember(data);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <TooltipCore>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button>
              <AddIcon />
              Add member
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Add New Member</TooltipContent>
      </TooltipCore>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Member</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" role="form">
            <Stack direction="column" gap="xl">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter member email" {...field} className="!al-m-0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Stack justify="end">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <LoadingButton type="submit" loading={isLoading}>
                  {isLoading ? "Adding..." : "Add Member"}
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewMember;
